import {
  Configuration,
  Custom,
  DvmData,
  DvmStyles,
  LegalTextData,
  Theme,
  Tutorial,
} from '@core/models';
import { checkoutTutorialSteps } from '../tutorial/checkout/steps';
import {
  seatSelectionDesktopTutorialSteps,
  seatSelectionMobileTutorialSteps,
} from '../tutorial/seat-selection/steps';

const custom: Custom = {
  paymentPage: {
    captureAddress: true,
    capturePhone: false,
  },
};

const theme: Theme = {
  'default-color': '00 44 97',
  'main-color': '00 44 97',
  'secondary-color': '235 132 31',
  'accent-color': '235 132 31',
  'tertiary-color': '242 246 250',
  red: '189 48 57',
  success: '86 163 67',
  'success-off-white': '236 246 232',
};

const dvmData: DvmData = {
  venueId: 'nam-us-10178-astros',
  mapId: 'main_venuemap',
  miniMapId: 'main_venuemap_minimap',
  miniMapEnabled: false,
  gaSectorsIdArray: [],
  gaAdaIdArray: [],
  sectionOffsets: {},
  limitedZoom: false,
};

const dvmStyles: DvmStyles = {
  'seat-available-none-fill': '25 149 255',
  'seat-available-none-stroke': '0 90 156',
  'seat-available-disabled-fill': '25 149 255',
  'seat-available-disabled-stroke': '0 90 156',
  'seat-unavailable-none': '214 214 214',
  'seat-available-pending': '230 145 56',
  'section-available-normal-fill': '25 149 255',
  'section-available-normal-stroke': '0 90 156',
  'section-available-normal-group1-fill': '255 77 77',
  'section-available-normal-group1-stroke': '255 77 77',
  'section-available-hover-fill': '0 90 156',
  'section-available-hover-stroke': '255 255 255',
  'section-selected-normal-fill': '225 56 63',
  'section-selected-normal-stroke': '225 56 63',
  'section-selected-hover-stroke': '255 255 255',
};

const seatSelectionDesktopTutorial: Tutorial = {
  sectionAvailabilityIds: ['1186', '1183', '1203', '1197', '1110'],
  availableSeatsQuantity: 240,
  selectedSeatsQuantity: 30,
  allowClose: false,
  doneBtnText: 'End Tutorial',
  nextBtnText: 'Next',
  popoverClass: 'driverjs-theme',
  prevBtnText: 'Back',
  steps: seatSelectionDesktopTutorialSteps,
};

const seatSelectionMobileTutorial: Tutorial = {
  ...seatSelectionDesktopTutorial,
  steps: seatSelectionMobileTutorialSteps,
};

const checkoutTutorial: Tutorial = {
  ...seatSelectionDesktopTutorial,
  steps: checkoutTutorialSteps,
};

export const HoustonAstrosConfig: Configuration = {
  id: 'houstonastros',
  clientName: 'Houston Astros',
  appName: 'Houston Astros',
  city: 'Houston',
  favicon: '/assets/img/houstonastros/favicon.png',
  logo: '/assets/img/houstonastros/logo.png',
  phone: '713-259-8350',
  email: 'tickethelp@astros.com',
  paymentMethod: 'braintree',
  rainout: false,
  upsells: true,
  clientWebUrl: 'https://www.astros.com/',
  clientWebText: 'https://www.astros.com/',
  termsOfUseUrl: 'https://www.mlb.com/official-information/terms-of-use',
  clientBallParkUrl:
    'https://www.mlb.com/astros/apps/ballpark?partnerId=redirect-chc-ballparkapp',
  clientBallParkText: 'astros.com/BallparkApp',
  mlbShopUrl:
    'https://www.mlbshopeurope.com/en/houston-astros/t-14553163+z-871253-930065732',
  merchandiseWebUrl: '',
  rainoutInsuranceUrl: '',
  termsAndConditionsUrl:
    'https://www.mlb.com/astros/tickets/ticket-back-terms-and-conditions',
  analytics: '',
  adas: true,
  custom,
  theme,
  dvmData,
  dvmStyles,
  tutorial: {
    seatSelection: {
      desktop: seatSelectionDesktopTutorial,
      mobile: seatSelectionMobileTutorial,
    },
    checkout: { desktop: checkoutTutorial, mobile: checkoutTutorial },
  },
  cartSummaryText: 'Here you can see your new requested tickets',
  extraCheckbox: {
    active: false,
    required: true,
    text: '',
  },
  summaryPage: {
    image1: '/assets/img/houstonastros/logo.png',
    image2: '',
  },
  get legalText(): LegalTextData {
    return {
      links: {
        termsLink: {
          href: this.termsAndConditionsUrl,
          text: 'Terms and Conditions',
          target: '_blank',
        },
        clubWebLink: {
          href: this.clientWebUrl,
          text: this.clientWebText,
          target: '_blank',
        },
      },
      text: [
        `As part of your ticket purchase, you are required to acknowledge the {termsLink}.
        The Terms and conditions of your ticket and policies applicable to its use are updated to
        address circumstances related to COVID-19, health and safety, and seating pods.
        By clicking the Place Order button, you agree to accept all {termsLink} applicable to ticket purchase, and all related policies.
        Further, due to the COVID-19 pandemic, scheduling may be impacted and games may be canceled,
        rescheduled, postponed or games may be held with limited capacity. Games may also have restricted capacity that prevents the use of specific
        seats. In the event that the game(s) for which the ticket(s) is issued does not occur or you are unable to use the
        specific seat(s) purchased, a refund or credit will be issued according to the refund policy available at {clubWebLink}.`,

        `By selecting the Place Order button you agree to purchase the tickets selected, to accept any restrictions noted, and
        understand you are responsible for any payment needed to complete the purchase. Upon completing your purchase by
        selecting the Place Order button, you acknowledge and agree your credit card will be charged. All sales are final. All
        tickets are subject to the {termsLink}.`,

        `In connection with your purchase, you may receive customer service-related emails from the ${this.clientName}, including
        optional surveys regarding your gameday experience. You also may receive ticket-related commercial email from the
        ${this.clientName}. You may unsubscribe from ticket-related commercial emails at any time.`,
      ],
    };
  },
  defaultTooltipText: 'Review seats before continuing',
  teamId: '117',
  metaPixelAnalytics: false,
};
